import React, { useState } from 'react';
import { Box, Typography, MenuItem, FormControl } from '@mui/material';
import NewModal from '../../../Shared/NewModal';
import CustomerCareSelect from '../../../Shared/CustomerCareSelect';
import { OwnerContact } from '../../../../store/hubOwner/types';
import LoadingSpinner from '../../../Shared/LoadingSpinner';
import {
  darkPurple,
  dimGrey,
  lightBlack,
  lightGrey,
} from '../../../../mainTheme';

const FACILITY_ROLE_KEYS = [
  'caregiver',
  'location_administrator',
  'facility_administrator',
];

const CARETEAM_ROLE_KEYS = ['administrator', 'caregiver', 'social_contact'];

const CARETEAM_ROLE_ID_MAP: Record<string, number> = {
  administrator: 2,
  caregiver: 3,
  social_contact: 4,
};

const CARETEAM_ROLE_LABEL_TO_KEY: Record<string, string> = {
  administrator: 'administrator',
  'family admin': 'administrator',
  'facility administrator': 'facility_administrator',
  'facility admin': 'facility_administrator',
  'location administrator': 'location_administrator',
  'location admin': 'location_administrator',
  caregiver: 'caregiver',
  'social contact': 'social_contact',
  'family caregiver': 'caregiver',
};

const normalizeRoleLabel = (label: string) =>
  label?.toLowerCase().replace(/_/g, ' ').trim();

const ROLE_OPTIONS = [
  // commenting out for excluding high-level ones in this view
  // {
  //   value: 'facility_administrator',
  //   label: 'Facility Administrator',
  //   description:
  //     'Control roles, changes, and medication schedules for all locations under a facility',
  // },
  {
    value: 'location_administrator',
    label: 'Location Administrator',
    description:
      'Control roles, changes, and medication schedules for one location under a facility',
  },
  {
    value: 'administrator',
    label: 'Administrator',
    description: 'Full access rights to an individual patient’s information.',
  },
  {
    value: 'caregiver',
    label: 'Caregiver',
    description: 'Provides care for patients via the Hub.',
  },
  {
    value: 'social_contact',
    label: 'Social Contact',
    description: 'An individual who may call the patient through the Hub.',
  },
] as const;

const TYPOGRAPHY_STYLES = {
  heading: {
    fontWeight: 500,
    fontSize: '22px',
    lineHeight: '30px',
    letterSpacing: '0.36px',
    color: darkPurple,
  },
  subheading: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '22px',
    letterSpacing: '0.2px',
    color: dimGrey,
  },
  menuItemLabel: {
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.2px',
    color: lightBlack,
  },
  menuItemDescription: {
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: 0,
    color: lightGrey,
  },
} as const;

interface EditRoleModalProps {
  onClose: () => void;
  contact: OwnerContact; // The selected careteam data
  onSave: (newRole: string | number) => void;
  isloading?: boolean;
}

const EditRoleModal: React.FC<EditRoleModalProps> = ({
  onClose,
  contact,
  onSave,
  isloading,
}) => {
  // Determine if this is a facility role or careteam role
  const isFacilityRole = !!contact.facility_role_id;
  const availableRoleKeys = isFacilityRole
    ? FACILITY_ROLE_KEYS
    : CARETEAM_ROLE_KEYS;
  const availableRoles = ROLE_OPTIONS.filter((role) =>
    availableRoleKeys.includes(role.value),
  );

  let defaultRole = '';
  if (contact?.careteam_role) {
    const normalized = normalizeRoleLabel(contact.careteam_role);
    defaultRole = CARETEAM_ROLE_LABEL_TO_KEY[normalized] || '';
  }

  const [selectedRole, setSelectedRole] = useState(defaultRole);

  const renderMenuItem = (option: (typeof ROLE_OPTIONS)[number]) => (
    <MenuItem
      key={option.value}
      value={option.value}
      sx={{ maxWidth: '396px', whiteSpace: 'normal' }}
    >
      <Box>
        <Typography sx={TYPOGRAPHY_STYLES.menuItemLabel}>
          {option.label}
        </Typography>
        <Typography
          sx={{
            ...TYPOGRAPHY_STYLES.menuItemDescription,
            whiteSpace: 'normal',
          }}
        >
          {option.description}
        </Typography>
      </Box>
    </MenuItem>
  );

  const handleSave = () => {
    if (isFacilityRole) {
      onSave(selectedRole);
    } else {
      // Pass role id for careteam api
      onSave(CARETEAM_ROLE_ID_MAP[selectedRole]);
    }
  };

  if (isloading) {
    return <LoadingSpinner />;
  }

  return (
    <NewModal
      title="Edit role"
      maxWidth="xs"
      showCloseX
      onClose={onClose}
      actions={{
        secondaryAction: {
          text: 'Cancel',
          onClick: onClose,
        },
        primaryAction: {
          text: 'Save',
          onClick: handleSave,
          disabled:
            !selectedRole ||
            selectedRole === defaultRole ||
            (isFacilityRole && defaultRole === 'facility_administrator'),
        },
      }}
    >
      <Box mb="24px">
        <Typography sx={TYPOGRAPHY_STYLES.heading} mb="8px">
          {contact.first_name} {contact.last_name}
        </Typography>
        <Typography sx={TYPOGRAPHY_STYLES.subheading} mb="24px">
          Select a role for this contact.
        </Typography>
        <FormControl fullWidth>
          <CustomerCareSelect
            label=""
            onChange={(e) => setSelectedRole(e.target.value)}
            fullWidth
            value={selectedRole}
            renderSelectedValue={(value) => {
              const option = ROLE_OPTIONS.find((opt) => opt.value === value);
              return option?.label ?? '';
            }}
          >
            {availableRoles.map(renderMenuItem)}
          </CustomerCareSelect>
        </FormControl>
      </Box>
    </NewModal>
  );
};

export default EditRoleModal;
